/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import { SiteContextInfo, SiteViewType, ViewTypeProps } from "../models";

const SITE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_NAME_LENGTH = 100;

export const isSiteViewType = (value: unknown): value is SiteViewType =>
  value === "list" || value === "assignPanel";

// SECURITY_NOTE: siteId ends up in a query string and a PUT body, so only a UUID-shaped value is accepted.
export const isValidSiteId = (value: unknown): value is string =>
  typeof value === "string" && SITE_ID_PATTERN.test(value);

const toSafeName = (value: unknown): string =>
  typeof value === "string" ? value.trim().slice(0, MAX_NAME_LENGTH) : "";

/**
 * Maps the appContext viewType to the site context. Returns null unless siteView is present
 * and valid, so every existing flow (no siteView) is left untouched.
 */
export const parseSiteContext = (viewType: ViewTypeProps): SiteContextInfo | null => {
  try {
    if (!viewType || !isSiteViewType(viewType.siteView) || !isValidSiteId(viewType.siteId)) {
      return null;
    }

    return {
      siteId: viewType.siteId,
      siteName: toSafeName(viewType.siteName),
      siteView: viewType.siteView,
      customerName: toSafeName(viewType.customerName),
    };
  } catch (error) {
    return null;
  }
};

/** Builds the site list query: always scoped to the customer and the selected site. */
export const buildSiteListQuery = (
  customerOrgId: string,
  siteId: string,
  extraQuery?: string,
): string =>
  [
    `servesto=${encodeURIComponent(customerOrgId)}`,
    `site=${encodeURIComponent(siteId)}`,
    extraQuery,
  ]
    .filter(Boolean)
    .join("&");
